import { useState, useEffect } from "react";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import {
  FORMAT_OPTIONS,
  DEFAULT_FORMAT,
  type ArtifactType,
} from "~/components/assistant/messages/artifactFormats";
import { Figure, Dataframe, DataValue } from "~/components/assistant/types";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { ExportProgress } from "./ExportProgress";
import { WorkbenchSelector } from "./WorkbenchSelector";
import { useExportArtifact } from "./hooks/useExportArtifact";

type ExportStage = "select" | "format" | "exporting" | "success" | "error";

interface WorkbenchOption {
  id: number;
  title: string;
}

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  artifact: Figure | Dataframe | DataValue;
  artifactType: ArtifactType;
  conversationId: string;
}

export function ExportDialog({
  open,
  onClose,
  artifact,
  artifactType,
  conversationId,
}: ExportDialogProps) {
  const [stage, setStage] = useState<ExportStage>("select");
  const [selectedWorkbench, setSelectedWorkbench] = useState<WorkbenchOption | null>(null);
  const [selectedFormat, setSelectedFormat] = useState(DEFAULT_FORMAT[artifactType]);
  const [error, setError] = useState<string | undefined>();
  const [exportedFileName, setExportedFileName] = useState<string | undefined>();
  const [workbenchPath, setWorkbenchPath] = useState<string | undefined>();
  const { contextId } = useAssistantContext();
  const { exportArtifact } = useExportArtifact();

  useEffect(() => {
    if (open) {
      setStage("select");
      setSelectedWorkbench(null);
      setSelectedFormat(DEFAULT_FORMAT[artifactType]);
      setError(undefined);
      setExportedFileName(undefined);
      setWorkbenchPath(undefined);
    }
  }, [open, artifactType]);

  const handleWorkbenchSelect = (workbench: WorkbenchOption) => {
    setSelectedWorkbench(workbench);
    setStage("format");
  };

  const handleExport = async () => {
    if (!selectedWorkbench) return;
    setStage("exporting");

    try {
      const result = await exportArtifact({
        artifact,
        artifactType,
        conversationId,
        projectId: selectedWorkbench.id,
        format: selectedFormat,
        contextId,
      });

      if (result.success) {
        setExportedFileName(result.fileName);
        setWorkbenchPath(result.workbenchPath);
        setStage("success");
      } else {
        setError(result.error);
        setStage("error");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Something went wrong while exporting your file."
      );
      setStage("error");
    }
  };

  const handleRetry = () => {
    setError(undefined);
    setStage("select");
  };

  const options = FORMAT_OPTIONS[artifactType];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        {stage === "select" && (
          <WorkbenchSelector onSelect={handleWorkbenchSelect} onCancel={onClose} />
        )}

        {stage === "format" && (
          <>
            <DialogHeader>
              <DialogTitle>Choose export format</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600 mb-4">
                Exporting to <span className="font-semibold">{selectedWorkbench?.title}</span>
              </p>
              <RadioGroup value={selectedFormat} onValueChange={setSelectedFormat}>
                {options.map((opt) => (
                  <RadioGroupItem
                    key={opt.value}
                    value={opt.value}
                    label={opt.label}
                    description={opt.description}
                    variant="card"
                    className="w-full"
                  />
                ))}
              </RadioGroup>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStage("select")}>
                Back
              </Button>
              <Button onClick={handleExport}>Export</Button>
            </div>
          </>
        )}

        {(stage === "exporting" || stage === "success" || stage === "error") && (
          <ExportProgress
            status={stage as "exporting" | "success" | "error"}
            artifactType={artifactType}
            fileName={exportedFileName}
            workbenchName={selectedWorkbench?.title}
            workbenchPath={workbenchPath}
            error={error}
            onClose={onClose}
            onRetry={handleRetry}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
